import { Fragment, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import WorkshopFeedbackTicker from "@/components/WorkshopFeedbackTicker";
import PMIconCarousel from "@/components/PMIconCarousel";
import { Reveal } from "@/components/Reveal";
import { SERVICES } from "@/lib/content";

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow="Our services"
        title="Three services. One outcome: a high performing culture."
        description="Each service stands alone — and each is stronger together. Together they take an organisation from honest assessment to lasting behaviour change."
      />

      <section className="bg-white py-20 lg:py-32">
        <div className="container-arms space-y-24 lg:space-y-32">
          {SERVICES.map((s, i) => (
            <Fragment key={s.id}>
            <article id={s.id} className="scroll-mt-28 grid items-center gap-10 lg:grid-cols-2" data-testid={`service-section-${s.id}`}>
              <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="relative overflow-hidden rounded-3xl group">
                  <img src={s.id === "performance-management" ? "/assets/pm-base-v2.png" : s.image} alt={s.alt} loading="lazy" className="aspect-[4/3] w-full object-cover img-duotone transition-transform duration-500 group-hover:scale-105" />
                  {s.id === "performance-management" && (
                    <>
                      <img
                        src="/assets/pm-cog-v2.png"
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        data-testid="pm-cog-rotating"
                        className="pm-cog-spin img-duotone absolute"
                        style={{ left: "13.191%", top: "25.783%", width: "37.845%" }}
                      />
                      <div className="pm-badge-orbit absolute inset-0" aria-hidden="true">
                        {[
                          { n: 1, left: "32.106%", top: "33.858%", color: "#032665" },
                          { n: 2, left: "42.756%", top: "43.453%", color: "#0154b9" },
                          { n: 3, left: "43.439%", top: "57.164%", color: "#026168" },
                          { n: 4, left: "32.169%", top: "68.297%", color: "#297e11" },
                          { n: 5, left: "20.981%", top: "57.173%", color: "#d88e21" },
                          { n: 6, left: "21.747%", top: "43.471%", color: "#9f0607" },
                        ].map((b) => (
                          <div
                            key={b.n}
                            data-testid={`pm-badge-${b.n}`}
                            className="pm-badge-stay absolute flex items-center justify-center rounded-full"
                            style={{ left: b.left, top: b.top, width: "5.25%", aspectRatio: "1", backgroundColor: b.color, boxShadow: "0 2px 6px rgba(0,0,0,0.25)" }}
                          >
                            <img src={`/assets/pm-num-${b.n}.png`} alt="" loading="lazy" className="h-full w-full object-contain" />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </Reveal>
              <Reveal delay={0.1} className={(i % 2 === 1 ? "lg:order-1" : "") + (s.id === "risk-evaluation" ? " relative" : "")}>
                {s.id === "risk-evaluation" && (
                  <div
                    data-testid="attitudes-bg"
                    aria-hidden="true"
                    className="pointer-events-none absolute right-0 top-24 z-0 aspect-square w-[250px] max-w-[56%] opacity-20 sm:opacity-30"
                  >
                    <div className="attitudes-bg-spin absolute inset-0">
                      {[
                        { n: 1, left: "50%", top: "16.67%" },
                        { n: 2, left: "78.87%", top: "33.33%" },
                        { n: 3, left: "78.87%", top: "66.67%" },
                        { n: 4, left: "50%", top: "83.33%" },
                        { n: 5, left: "21.13%", top: "66.67%" },
                        { n: 6, left: "21.13%", top: "33.33%" },
                      ].map((b) => (
                        <img
                          key={b.n}
                          src={`/assets/icons/attitude-a${b.n}.png`}
                          alt=""
                          loading="lazy"
                          className="attitudes-icon-stay absolute"
                          style={{ left: b.left, top: b.top, width: "39.17%" }}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div className={s.id === "risk-evaluation" ? "relative z-10" : undefined}>
                <div className="mb-4 flex items-center gap-4">
                  <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-white shadow-sm">
                    <img src={s.icon} alt="" loading="lazy" className="h-full w-full object-cover" />
                  </span>
                  <p className="eyebrow">Service {s.num}</p>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">{s.title}</h2>
                <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">{s.detail}</p>
                <ul className="mt-7 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal/10 text-teal">
                        <Check size={14} />
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
                {s.id === "performance-management" && <PMIconCarousel />}
                <Link
                  to="/contact"
                  data-testid={`service-enquire-${s.id}`}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-navy"
                >
                  Enquire about this service <ArrowRight size={16} />
                </Link>
                </div>
              </Reveal>
            </article>
            {s.id === "actor-led-workshops" && (
              <div className="-mt-14 lg:-mt-20">
                <WorkshopFeedbackTicker />
              </div>
            )}
            </Fragment>
          ))}
        </div>
      </section>

      <CtaBand
        title="Not sure which service fits?"
        text="Tell us what's happening in your organisation. We'll recommend an honest starting point — even if it's a smaller one than you expected."
      />
    </div>
  );
}
